import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { IQualificationBasis } from 'src/app/model/qualifications/iqualification-basis';
import { IQualificationBasisDef } from 'src/app/model/view/iqualification-basis-def';
import { QUALIFICATION_NAMES } from 'src/app/service/ansokan-form-builder/application-names';
import { QUALIFICATION_BASIS_FORM_DEF } from 'src/app/service/ansokan-form-builder/form-definitions';

@Component({
    selector: 'app-qualification-basis',
    styleUrls: ['./qualification-basis.component.scss'],
    templateUrl: './qualification-basis.component.html',
    standalone: false
})
export class QualificationBasisComponent implements OnInit {

  private readonly NAMES = QUALIFICATION_NAMES.BASES;
  @Input() public availableBases: IQualificationBasisDef[];
  @Input() public selectedBases: UntypedFormArray;

  constructor(private fb: UntypedFormBuilder) { }

  public get showYearIntervals(): (basis: IQualificationBasisDef) => boolean {
    return (basis: IQualificationBasisDef) => basis.yearIntervals && this.isSelected(basis);
  }

  public get showYearFreeMax(): (basis: IQualificationBasisDef) => boolean {
    return (basis: IQualificationBasisDef) => basis.yearFreeMax && this.isSelected(basis);
  }

  public get fromYears(): (basis: IQualificationBasisDef) => number[] {
    return (basis: IQualificationBasisDef) => {
      const years = [];
      for (let i = 1; i <= basis.yearFreeMax; i++) {
        years.push(i);
      }
      return years;
    };
  }

  public get toYears(): (basis: IQualificationBasisDef) => number[] {
    return (basis: IQualificationBasisDef) => {
      const fromYearControl = this.toFromYearControl(basis);
      const fromYear = fromYearControl.value ? fromYearControl.value : 1;
      const years = [];
      for (let i = fromYear; i <= basis.yearFreeMax; i++) {
        years.push(i);
      }
      return years;
    };
  }

  public ngOnInit() {
  }

  public toControl(basis: IQualificationBasisDef): UntypedFormControl {
    const control = this.fb.control(this.isSelected(basis));
    control.valueChanges.subscribe((value) => this.toggle(basis, value));
    return control;
  }

  public toggle(basis: IQualificationBasisDef, checked: boolean): void {
    if (checked) {
      this.selectedBases.push(this.toQualificationBasisFormGroup(basis));
    } else {
      const index = this.indexOfBasis(basis);
      if (index !== -1) {
        this.selectedBases.removeAt(index);
      }
    }
  }

  public toIntervalControl(basis: IQualificationBasisDef, interval: string): UntypedFormControl {
    const control = this.fb.control(this.isIntervalSelected(basis, interval));
    control.valueChanges.subscribe((checked) => this.toggleInterval(checked, basis, interval));
    return control;
  }

  public toggleInterval(checked: boolean, basis: IQualificationBasisDef, interval: string): void {
    const intervals = this.getBasisIntervals(basis);
    if (checked) {
      // Add interval if not already added.
      if (!this.isIntervalSelected(basis, interval)) {
        intervals.push(this.fb.control(interval));
      }
    } else {
      // Get interval form array.
      const index = this.indexOfInterval(basis, interval);
      if (index !== -1) {
        // Remove interval.
        intervals.removeAt(index);
      }
    }
  }

  public toFromYearControl(basis: IQualificationBasisDef): UntypedFormControl {
    // Get fromYear control.
    const control = this.toYearControl(basis, this.NAMES.YEAR_FROM_OPTIONAL);
    // when from year changes, reset toYear if its value is less than fromYear.
    control.valueChanges.subscribe((value) => {
      const basisIndex = this.indexOfBasis(basis);
      const toYear = this.selectedBases.at(basisIndex).get(this.NAMES.YEAR_TO_OPTIONAL);
      toYear.setValue(toYear.value && toYear.value >= value ? toYear.value : null);
    });
    return control;
  }

  public toToYearControl(basis: IQualificationBasisDef): UntypedFormControl {
    return this.toYearControl(basis, this.NAMES.YEAR_TO_OPTIONAL);
  }

  private toYearControl(basis: IQualificationBasisDef, controlName: string): UntypedFormControl {
    const basisIndex = this.indexOfBasis(basis);
    const basisGroup = this.selectedBases.at(basisIndex) as UntypedFormGroup;
    const yearControl = basisGroup.get(controlName);
    const control = new UntypedFormControl(yearControl.value);
    control.valueChanges.subscribe((value) => yearControl.setValue(value));
    return control;
  }

  private isIntervalSelected(basis: IQualificationBasisDef, interval: string): boolean {
    return this.indexOfInterval(basis, interval) !== -1;
  }

  private indexOfInterval(basis: IQualificationBasisDef, interval: string): number {
    // Get intervals.
    const intervals = this.getBasisIntervals(basis).value;
    // find index of interval.
    return intervals.findIndex((i) => i === interval);
  }

  private getBasisIntervals(basis: IQualificationBasisDef): UntypedFormArray {
    // Get index of basis.
    const basisIndex = this.indexOfBasis(basis);
    // Get interval form array.
    return this.selectedBases.at(basisIndex).get(this.NAMES.YEAR_INTERVALS_OPTIONAL) as UntypedFormArray;
  }

  private isSelected(selectedBasis: IQualificationBasisDef): boolean {
    return this.indexOfBasis(selectedBasis) !== -1;
  }

  private indexOfBasis(basis: IQualificationBasisDef): number {
    const values = this.selectedBases.value as IQualificationBasis[];
    return values.findIndex((current: IQualificationBasis) => current.typeCode === basis.typeCode);
  }

  private toQualificationBasisFormGroup(basis: IQualificationBasisDef): UntypedFormGroup {
    const hasIntervals = basis.yearIntervals ? true : false;
    const hasFreeMax = basis.yearFreeMax ? true : false;
    const group = this.fb.group(QUALIFICATION_BASIS_FORM_DEF(hasIntervals, hasFreeMax));
    group.patchValue(basis);
    return group;
  }
}
